import { useCallback, useEffect, useRef } from 'react';
import { useUsageLogService } from '@actschurch/ccms-sdk';
import { getAnonymousId } from '@/utils/anonymousId';
import { getDeviceInfo } from '@/utils/deviceInfo';

export interface SessionTelemetryContext {
  sessionId: number;
  courseId?: number;
  ministryId?: number;
}

export interface SessionTelemetry {
  /** Report playback progress. Safe to call every second — the hook throttles to one network call per 15s. */
  onProgress: (viewDurationSeconds: number, percentWatched: number) => void;
  /** Report completion. Flushes any pending heartbeat then sends the final event. */
  onCompleted: (viewDurationSeconds: number, percentWatched: number) => void;
}

const PING_WINDOW_MS = 15_000;
const MIN_DELTA_SECONDS = 5;

/**
 * Session telemetry hook for the video/audio/text/pdf player pages.
 *
 * - On mount: posts an <c>open</c> event (anonymous id + device info attached).
 * - During playback: debounces progress updates to one <c>ping</c> per 15 seconds,
 *   and only when the viewer has actually advanced at least 5 real seconds (guards
 *   against scrubs and idle tabs inflating the watched count).
 * - On <c>visibilitychange === 'hidden'</c> or <c>pagehide</c>: fires
 *   <c>navigator.sendBeacon</c> with the latest progress — backend coalesces it
 *   into the live UsageLog row.
 * - <c>onCompleted</c>: flushes any pending ping then posts the final event.
 */
export function useSessionTelemetry(ctx: SessionTelemetryContext | null): SessionTelemetry {
  const usageLogService = useUsageLogService();

  // Snapshot of the last progress the view layer supplied.
  const latestProgressRef = useRef<{ viewDurationSeconds: number; percentWatched: number } | null>(null);
  // Snapshot of what we most recently posted upstream.
  const lastSentRef = useRef<{ viewDurationSeconds: number; at: number } | null>(null);
  // Pending debounce timer.
  const timerRef = useRef<number | null>(null);

  // Fire an open event exactly once per (sessionId) mount.
  useEffect(() => {
    if (!ctx) return;
    const device = getDeviceInfo();
    void usageLogService.recordOpen({
      sessionID: ctx.sessionId,
      courseID: ctx.courseId,
      entityType: 'Session',
      entityID: ctx.sessionId,
      ministryID: ctx.ministryId,
      anonymousID: getAnonymousId(),
      deviceType: device.deviceType,
      os: device.os,
      browser: device.browser,
      userAgent: device.userAgent,
    });
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.sessionId]);

  // On page hide, flush via sendBeacon (reliable during unload).
  useEffect(() => {
    if (!ctx) return;
    const flush = () => {
      const progress = latestProgressRef.current;
      if (!progress) return;
      usageLogService.beacon({
        sessionID: ctx.sessionId,
        courseID: ctx.courseId,
        entityType: 'Session',
        entityID: ctx.sessionId,
        ministryID: ctx.ministryId,
        anonymousID: getAnonymousId(),
        viewDurationSeconds: progress.viewDurationSeconds,
        percentWatched: progress.percentWatched,
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [ctx, usageLogService]);

  const schedulePing = useCallback(() => {
    if (timerRef.current != null || !ctx) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      const progress = latestProgressRef.current;
      if (!progress) return;
      const last = lastSentRef.current;
      if (last && progress.viewDurationSeconds - last.viewDurationSeconds < MIN_DELTA_SECONDS) {
        return; // Not enough actual playback to warrant a network call.
      }
      void usageLogService.recordPing({
        sessionID: ctx.sessionId,
        courseID: ctx.courseId,
        entityType: 'Session',
        entityID: ctx.sessionId,
        ministryID: ctx.ministryId,
        anonymousID: getAnonymousId(),
        viewDurationSeconds: progress.viewDurationSeconds,
        percentWatched: progress.percentWatched,
      });
      lastSentRef.current = {
        viewDurationSeconds: progress.viewDurationSeconds,
        at: Date.now(),
      };
    }, PING_WINDOW_MS);
  }, [ctx, usageLogService]);

  const onProgress = useCallback(
    (viewDurationSeconds: number, percentWatched: number) => {
      latestProgressRef.current = { viewDurationSeconds, percentWatched };
      schedulePing();
    },
    [schedulePing],
  );

  const onCompleted = useCallback(
    (viewDurationSeconds: number, percentWatched: number) => {
      if (!ctx) return;
      // Cancel any pending debounce — we're sending the terminal event now.
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      latestProgressRef.current = { viewDurationSeconds, percentWatched };
      void usageLogService.recordCompleted({
        sessionID: ctx.sessionId,
        courseID: ctx.courseId,
        entityType: 'Session',
        entityID: ctx.sessionId,
        ministryID: ctx.ministryId,
        anonymousID: getAnonymousId(),
        viewDurationSeconds,
        percentWatched,
      });
    },
    [ctx, usageLogService],
  );

  return { onProgress, onCompleted };
}
